import React from 'react';
import Paper from '@material-ui/core/Paper';
import { t } from 'ttag';

import DataTable from '../../../components/DataTable';
import { BASE_STATUS_TITLES, USER_STATUS_OPTIONS } from '../../../utils/enums';
import useApi, { MyClubCApi } from '../../../api';
import useUser from '../../../auth/hooks';
import { insertAt } from '../../../utils/helpers';
import useRouter from 'use-react-router';
import { useApolloClient } from '@apollo/react-hooks';
import queryString from 'query-string';
import useNotifier from '../../../components/Messages/hooks';
import { extractDataError } from '../../../utils/errors';
import { useNavigator } from '../../../components/Navigator';
import Grid from '@material-ui/core/Grid';
import Pagination from '@material-ui/lab/Pagination/Pagination';
import { MTableToolbar } from 'material-table';
import Tooltip from '@material-ui/core/Tooltip/Tooltip';
import FilterListIcon from '@material-ui/icons/FilterList';
import Autocomplete from '@material-ui/lab/Autocomplete/Autocomplete';
import TextField from '@material-ui/core/TextField/TextField';
import Typography from '@material-ui/core/Typography';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import Loading from '../../../components/Loading';

const SearchInput = ({ value, onChange }) => {
  const [searchQuery, setSearchQuery] = React.useState(value);
  return (
    <>
      <Grid
        container
        item
        spacing={1}
        justifyContent='flex-start'
        alignItems='center'
      >
        <Grid item sm>
          <TextField
            size='small'
            label={t`Пошук`}
            variant='outlined'
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  {searchQuery ? (
                    <IconButton
                      size='small'
                      aria-label='toggle password visibility'
                      onClick={() => onChange(null)}
                      edge='end'
                    >
                      <ClearIcon />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item sm={1}>
          <Tooltip title={t`Застосувати пошук`}>
            <IconButton onClick={() => onChange(searchQuery)}>
              <SearchIcon />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );
};

const ListView = () => {
  const navigate = useNavigator();
  const { user } = useUser();
  const api = useApi();

  const { history } = useRouter();
  const client = useApolloClient();
  const newApi = new MyClubCApi(client);

  const [loading, setLoading] = React.useState(false);
  const [managers, setManagers] = React.useState([]);
  const [pageInfo, setPageInfo] = React.useState({});

  const searchParams = new URLSearchParams(history.location.search);
  const [pageState, setPageState] = React.useState({
    status: parseInt(searchParams.get('status'), 10) || undefined,
    page: parseInt(searchParams.get('page'), 10) || 1,
    query: searchParams.get('query') || undefined,
    limit: 50,
  });

  const updateHistory = (searchParams) => {
    history.replace({
      search: `?${queryString.stringify(searchParams)}`,
    });
  };

  const pushMessage = useNotifier();

  const fetchManagers = async () => {
    setLoading(true);
    const finalParams = Object.fromEntries(
      Object.entries(pageState).filter(([, value]) => !!value)
    );
    updateHistory(finalParams);
    const data = await newApi.getPaginatedManagers(finalParams);
    setManagers(data.items);
    setPageInfo(data.pageInfo);
    setLoading(false);
  };

  React.useEffect(() => {
    fetchManagers();
  }, [pageState]);

  const onEdit = (e, data) => {
    navigate(api.router.managers.edit(data.id));
  };

  const onAdd = () => {
    navigate(api.router.managers.create());
  };

  const onDelete = async (data) => {
    const res = await newApi.deleteManager(data.id);
    if (res.ok) {
      pushMessage({ text: t`Успішно видалено` });
    } else {
      const dataError = extractDataError(res.errors);
      pushMessage({ text: dataError });
    }
    await fetchManagers();
  };

  const handleSetPage = async (e, p) => {
    const newPageState = { ...pageState, page: p };
    setPageState(newPageState);
  };

  const handleSetFilter = async (name, value) => {
    const newPageState = { ...pageState, page: 1, [name]: value };
    setPageState(newPageState);
  };

  const columns = [
    { title: t`Id`, field: 'id', editable: 'never' },
    { title: t`Прізвище`, field: 'lastName' },
    { title: t`Ім'я`, field: 'firstName' },
    { title: t`По-батькові`, field: 'middleName' },
    { title: t`Email`, field: 'email' },
    {
      title: t`Статус`,
      field: 'status',
      render: ({ status }) => BASE_STATUS_TITLES[status],
    },
  ];
  const adminColumns = [
    { title: t`Клуб`, field: 'club', render: ({ club }) => club.shortName },
  ];
  user.isAdmin && insertAt(columns, 1, ...adminColumns);

  if (loading) return <Loading />;

  return (
    <Paper>
      <DataTable
        search={false}
        pageSize={Math.min(pageState.limit, managers.length)}
        isLoading={loading}
        title={t`Список адміністраторів`}
        columns={columns}
        data={managers}
        onAdd={onAdd}
        onEdit={onEdit}
        onDelete={onDelete}
        components={{
          Pagination: () => (
            <Grid container style={{ marginTop: '10px', marginBottom: '10px' }}>
              <Grid item style={{ margin: '0 auto' }}>
                <Pagination
                  size='medium'
                  count={pageInfo.pages}
                  page={pageState.page}
                  color='primary'
                  onChange={handleSetPage}
                />
              </Grid>
            </Grid>
          ),
          Toolbar: (props) => (
            <div style={{ flexGrow: 1 }}>
              <MTableToolbar {...props} />
              <Grid
                container
                spacing={2}
                style={{ marginLeft: '10px' }}
                justifyContent='flex-start'
                alignItems='center'
              >
                <Grid item>
                  <Tooltip title={t`Виберіть фільтр`}>
                    <FilterListIcon />
                  </Tooltip>
                </Grid>
                <Grid item sm={2}>
                  <Autocomplete
                    size='small'
                    options={USER_STATUS_OPTIONS}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t`Виберіть статус`}
                        variant='outlined'
                      />
                    )}
                    value={USER_STATUS_OPTIONS.find(
                      (item) => item && item.id === pageState.status
                    )}
                    onChange={(e, value) =>
                      handleSetFilter('status', value && value.id)
                    }
                  />
                </Grid>
                <Grid item sm={2}>
                  <SearchInput
                    value={pageState.query}
                    onChange={(value) => handleSetFilter('query', value)}
                  />
                </Grid>
              </Grid>
              {!loading && (
                <Grid container style={{ margin: '10px 0 0 15px' }}>
                  <Grid item>
                    <Typography>
                      {t`Знайдено `} <b>{pageInfo.total}</b>{' '}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </div>
          ),
        }}
      />
    </Paper>
  );
};

export default ListView;
